import React, { useState, useRef } from "react";
import { Upload, X, FileText, Image, Video, HelpCircle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/use-toast";

interface UploadedFile {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
}

const ACCEPTED_FORMATS = ".md,.pdf,.txt,.csv,.xlsx,.json,.jpg,.jpeg,.png";
const ACCEPTED_DISPLAY = ".md, .pdf, .txt, .csv, .xlsx, .json, .jpg, .png";

// URL regex pattern
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;

const extractUrls = (text: string): string[] => {
  const matches = text.match(URL_REGEX);
  return matches ? [...new Set(matches)] : [];
};

const ResourceSubmissionForm: React.FC = () => {
  const { organization } = useOrganization();
  const [textInput, setTextInput] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [shareFiles, setShareFiles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const newFiles: UploadedFile[] = Array.from(selectedFiles).map(file => ({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      type: file.type,
      size: file.size
    }));

    setFiles(prev => [...prev, ...newFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="h-4 w-4" />;
    if (type.includes("pdf")) return <FileText className="h-4 w-4 text-red-400" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    
    const trimmedText = textInput.trim();
    if (!trimmedText && files.length === 0) return;

    setIsSubmitting(true);

    try {
      // Get current user (if logged in)
      const { data: { user } } = await supabase.auth.getUser();

      // Generate a unique ID for this pending resource
      const pendingResourceId = crypto.randomUUID();

      // Extract URLs from text
      const extractedUrls = extractUrls(trimmedText);

      // Upload files to storage
      const uploadedFilePaths: string[] = [];
      
      for (const fileItem of files) {
        const filePath = `${pendingResourceId}/${fileItem.name}`;
        
        const { error: uploadError } = await supabase.storage
          .from('pending-resources')
          .upload(filePath, fileItem.file);

        if (uploadError) {
          console.error('File upload error:', uploadError);
          throw new Error(`Failed to upload ${fileItem.name}`);
        }

        uploadedFilePaths.push(filePath);
      }

      // Insert into pending_resources table with organization_id
      const { error: insertError } = await supabase
        .from('pending_resources')
        .insert({
          id: pendingResourceId,
          text_input: trimmedText || null,
          files: uploadedFilePaths,
          urls: extractedUrls,
          user_id: user?.id || null,
          organization_id: organization?.id || null,
          status: 'pending',
          share_source_files: shareFiles && uploadedFilePaths.length > 0
        });

      if (insertError) {
        console.error('Insert error:', insertError);
        throw new Error('Failed to save submission');
      }

      // Trigger AI processing in background (fire-and-forget)
      supabase.functions.invoke('process-pending-resource', {
        body: { id: pendingResourceId }
      }).catch(err => {
        console.error('Background processing error:', err);
      });

      // Success - clear form and show toast
      setTextInput("");
      setFiles([]);
      setShareFiles(false);
      
      toast({
        title: "Thanks!",
        description: "Your resource is being processed.",
      });

    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Submission failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || (!textInput.trim() && files.length === 0);

  return (
    <div className="bg-card border border-border rounded-xl p-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
      <h3 className="font-bold text-sm mb-2 flex items-center gap-2">
        <Upload className="h-4 w-4 text-primary" />
        Submit a Resource
      </h3>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Paste text, links, or upload files. Our AI-powered backend will create a full resource with summaries, tags, and SEO metadata.
      </p>

      {/* Text Input */}
      <Textarea
        placeholder="Describe your resource or paste a link…"
        value={textInput}
        onChange={e => setTextInput(e.target.value)}
        disabled={isSubmitting}
        className="min-h-[80px] mb-3 resize-none bg-background/50 border-border/50 focus:border-primary/50 text-sm"
      />

      {/* File Upload Area */}
      <div className="mb-3">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FORMATS}
          multiple
          onChange={handleFileChange}
          disabled={isSubmitting}
          className="hidden"
          id="resource-file-upload"
        />
        <label
          htmlFor="resource-file-upload"
          className={`flex items-center justify-center gap-2 border border-dashed border-border/60 rounded-lg p-3 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <Upload className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Click to upload files
          </span>
        </label>
        <p className="text-[10px] text-muted-foreground/70 mt-1.5 flex items-center gap-1">
          <span>Accepted: {ACCEPTED_DISPLAY}</span>
          <span className="mx-1">•</span>
          <Video className="h-3 w-3 inline" />
          <span>or paste video URLs</span>
        </p>
      </div>

      {/* File Preview List */}
      {files.length > 0 && (
        <div className="space-y-2 mb-3 max-h-[120px] overflow-y-auto">
          {files.map(file => (
            <div
              key={file.id}
              className="flex items-center gap-2 bg-muted/30 rounded-md px-2.5 py-1.5 text-xs group animate-in fade-in slide-in-from-left-2 duration-200"
            >
              {getFileIcon(file.type)}
              <span className="flex-1 truncate" title={file.name}>
                {file.name}
              </span>
              <span className="text-muted-foreground text-[10px]">
                {formatFileSize(file.size)}
              </span>
              <button
                onClick={() => removeFile(file.id)}
                disabled={isSubmitting}
                className="opacity-50 hover:opacity-100 transition-opacity p-0.5 hover:bg-destructive/20 rounded disabled:pointer-events-none"
              >
                <X className="h-3.5 w-3.5 text-destructive" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Share Files Checkbox - only shown when files are uploaded */}
      {files.length > 0 && (
        <div className="flex items-start gap-2 mb-3 p-2.5 bg-muted/20 rounded-lg border border-border/50">
          <Checkbox
            id="share-files"
            checked={shareFiles}
            onCheckedChange={(checked) => setShareFiles(checked === true)}
            disabled={isSubmitting}
            className="mt-0.5"
          />
          <div className="flex-1">
            <Label 
              htmlFor="share-files" 
              className="text-xs font-medium cursor-pointer"
            >
              Allow others to view the original file(s)
            </Label>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              If checked, users can download the original document from the library
            </p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        className="w-full mb-2"
        size="sm"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Submit Resource
          </>
        )}
      </Button>

      {/* What Happens Next Modal */}
      <Dialog>
        <DialogTrigger asChild>
          <button className="w-full text-xs text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1">
            <HelpCircle className="h-3 w-3" />
            What happens next?
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              What Happens After Submission
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
                1
              </div>
              <div>
                <p className="text-sm font-medium">AI Processing</p>
                <p className="text-xs text-muted-foreground">
                  Our AI extracts useful information from your submission, including key topics and context.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
                2
              </div>
              <div>
                <p className="text-sm font-medium">Summary & Tags</p>
                <p className="text-xs text-muted-foreground">
                  AI generates a compelling summary, identifies relevant tags, and creates SEO-optimized metadata.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">
                3
              </div>
              <div>
                <p className="text-sm font-medium">Moderator Review</p>
                <p className="text-xs text-muted-foreground">
                  A moderator reviews the formatted resource before it's published to the library.
                </p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceSubmissionForm;
